/** @format */
import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { StudyRight } from 'common-typescript/types';
import { Observable, of, shareReplay, ShareReplayConfig, startWith } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ComponentDowngradeMappings, DowngradedComponent, StaticMembers } from 'sis-common';
import { AppErrorHandler, StudyRightExpirationInfo, trackByNestedEntityId, UniversityService } from 'sis-components';
import { StudyRightExtensionApplyingService } from '../../common/service/study-right-extension-applying.service';
import { StudyRightExpirationInfoService } from './study-right-expiration-info.service';

@StaticMembers<DowngradedComponent>()
@Component({
    selector: 'app-study-right-expiration-info',
    templateUrl: './study-right-expiration-info.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class StudyRightExpirationInfoComponent {
    static downgrade: ComponentDowngradeMappings = {
        moduleName: 'student.frontpage.study-right-expiration-info',
        directiveName: 'appStudyRightExpirationInfo',
    };

    private readonly studyRightExtensionApplyingService = inject(StudyRightExtensionApplyingService);
    private readonly universityService = inject(UniversityService);
    private readonly studyRightExpirationInfoService: StudyRightExpirationInfoService = inject(StudyRightExpirationInfoService);
    private readonly appErrorHandler: AppErrorHandler = inject(AppErrorHandler);

    readonly expirationInfos$ = this.getStudyRightExpirationInfos();
    readonly extensionApplicationEnabled = toSignal(
        this.universityService.getCurrentUniversitySettings().pipe(
            map(settings => settings?.frontendFeatureToggles?.studyRightExtensionApplicationEnabled ?? true),
            catchError(() => of(true)),
            startWith(true),
        ),
    );

    readonly studyRightId = trackByNestedEntityId<StudyRightExpirationInfo>('studyRight');

    applyForExtension(studyRight: StudyRight) {
        this.studyRightExtensionApplyingService.checkRequirementsAndProceedToApplication(studyRight);
    }

    private getStudyRightExpirationInfos(): Observable<readonly StudyRightExpirationInfo[]> {
        return this.studyRightExpirationInfoService.studyRightExpirationInfos$.pipe(
            this.appErrorHandler.defaultErrorHandler(),
            shareReplay(<ShareReplayConfig>{ bufferSize: 1, refCount: true }),
        );
    }
}
